import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isEqual } from 'lodash';

import { BasicAssumption } from '@futrli-sdk/predict';
import { useCrudActivityContext, usePromiseState } from '@futrli/shared-services';

import {
  ASSUMPTION_TYPES,
  ASSUMPTION_TYPES_URL_PARAMS,
  LAST_DAY_OF_MONTH,
  TRACKING_EVENTS
} from '../../../../../core/constants';
import {
  checkIfFreestyleContentIsEmpty,
  generateFinancialYearsData,
  getFormattedAmounts,
  getFreestyleDataContent,
  getFreestyleInitialContent,
  getUpdateFreestyleInitialContent
} from './utils';
import { fetchAssumptionAmounts, updateAssumptionData } from '../../../../../core/api/assumption';
import { AllowedAssumptionTypes } from '../../../../detail/reports/actions/actions';
import { AssumptionData } from '../../../../../core/types';
import { FreestyleMethodState, PaymentTreatment, PredictionType } from '../../../types';
import { FreestyleContent } from '../../../components/freestyle/freestyle-content';
import { PredictionEditor } from '../prediction-editor/prediction-editor';
import { PredictionHeader } from '../prediction-header/prediction-header';
import { PredictManagementContext } from '../../../../../predict-management/context/predict-management-context';
import { trackEvent } from '../../../../../core/tracker';
import { UserGridSettingsContext } from '../../../../detail/reports/user-grid-settings/user-grid-settings-context';
import { useStandardOrganisationPredictionBuilderContext } from '../../../context/standard-organisation-prediction-builder-context';
import { usePredictionsInfoContext } from '../../../../detail/context/predictions-info-context';

interface Props {
  onClose?: () => void;
  isPredictionTypeActive?: boolean;
}

export const FreestylePrediction = ({ onClose, isPredictionTypeActive }: Props) => {
  const {
    accountIndex,
    financialConfig,
    handleSaveExisting,
    handleSaveNew,
    isCashFlowDisabled,
    isEditing,
    isQuickCopy,
    paymentDataSaveParams,
    paymentTreatment,
    predictionData,
    predictionName,
    predictionType,
    reloadForecastData,
    reset,
    resetUrlData,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    urlPredictionId,
    urlPredictionType,
    vatPercentage,
    previewImpactsResponse
  } = useStandardOrganisationPredictionBuilderContext();

  const { addErrorMessage, currency } = useContext(PredictManagementContext);
  const { addCreating, removeCreating } = useCrudActivityContext();
  const { forecast } = usePredictionsInfoContext();
  const { showDecimals } = useContext(UserGridSettingsContext);
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisatioId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisatioId || '';

  const [freestyleContent, setFreestyleContent] = useState<FreestyleMethodState>();
  const [storeOnThe, setStoreOnThe] = useState('1');

  const [, setFreestyleValuesReq, freestyleValues] = usePromiseState<AssumptionData[]>();

  const forecastFromDate = forecast?.forecast_from_date;
  const isActive = predictionType === PredictionType.FREESTYLE;
  const isActiveInUrl = urlPredictionType === ASSUMPTION_TYPES_URL_PARAMS[ASSUMPTION_TYPES.BASIC];
  const isFirstRun = useRef(true);
  const lastImpactPreviewParams = useRef<Partial<AllowedAssumptionTypes>>();

  // *********
  // * Utils *
  // *********

  const loadContent = useCallback(async () => {
    if (forecast?.id && urlPredictionId && isActiveInUrl) {
      try {
        await setFreestyleValuesReq(
          fetchAssumptionAmounts<AssumptionData[]>(
            workspaceId,
            organisationId,
            forecast.id,
            ASSUMPTION_TYPES.BASIC,
            urlPredictionId
          )
        );
      } catch (e) {
        addErrorMessage?.(e as Error);
      }
    }
  }, [
    addErrorMessage,
    forecast?.id,
    isActiveInUrl,
    organisationId,
    setFreestyleValuesReq,
    urlPredictionId,
    workspaceId
  ]);

  const refreshAccountsImpactPreview = useCallback(async () => {
    if (accountIndex && !isCashFlowDisabled) {
      const newParams = getFreestyleDataContent({
        predictionName: 'impact-data-request', // Dont use real predictionName here as it causes unneccesary refreshes
        vatPercentage,
        freestyleContent,
        accountIndex,
        storeOnThe,
        paymentTreatment
      });
      if (!isEqual(newParams, lastImpactPreviewParams.current)) {
        lastImpactPreviewParams.current = newParams;
        setImpactPreviewParams(newParams);
      }
    }
  }, [
    accountIndex,
    isCashFlowDisabled,
    vatPercentage,
    freestyleContent,
    storeOnThe,
    paymentTreatment,
    setImpactPreviewParams
  ]);

  const restoreInitialContent = useCallback(() => {
    if (financialConfig) {
      setFreestyleContent(getFreestyleInitialContent(financialConfig, forecastFromDate, forecast?.kind));
      setStoreOnThe('1');
      return true;
    }
    return false;
  }, [financialConfig, forecastFromDate, forecast?.kind]);

  const restoreLoadedContent = useCallback(() => {
    if (financialConfig && predictionData && freestyleValues) {
      setStoreOnThe((predictionData as BasicAssumption).invoice_date || '1');
      setFreestyleContent(
        getUpdateFreestyleInitialContent(
          financialConfig,
          freestyleValues,
          showDecimals,
          forecastFromDate,
          forecast?.kind
        )
      );
      return true;
    }
    return false;
  }, [financialConfig, forecastFromDate, forecast?.kind, freestyleValues, predictionData, showDecimals]);

  const saveAssumptionData = useCallback(
    async (id: string) => {
      if (freestyleContent) {
        try {
          await updateAssumptionData(
            getFormattedAmounts(freestyleContent),
            workspaceId,
            organisationId,
            forecast!.id,
            ASSUMPTION_TYPES.BASIC,
            id
          );
          return true;
        } catch (e) {
          addErrorMessage?.(e as Error);
        }
      }
      return false;
    },
    [addErrorMessage, forecast, freestyleContent, organisationId, workspaceId]
  );

  const save = useCallback(async () => {
    const params = {
      name: predictionName,
      vat_percentage: paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER ? '0' : vatPercentage || null,
      account_id: accountIndex,
      excluded_from_report: false,
      invoice_date: storeOnThe === LAST_DAY_OF_MONTH ? LAST_DAY_OF_MONTH : Number(storeOnThe),
      payment_type_data: paymentDataSaveParams
    };

    if (urlPredictionId && freestyleContent && !isQuickCopy) {
      // Update existing
      const [updateSuccess, updateValuesSuccess] = await Promise.all([
        handleSaveExisting(params),
        saveAssumptionData(urlPredictionId)
      ]);
      return updateSuccess && updateValuesSuccess;
    } else {
      // Save new
      const newAssumption = await handleSaveNew(params);
      if (newAssumption) {
        const updateValuesSuccess = await saveAssumptionData(newAssumption.id);
        if (updateValuesSuccess) {
          trackEvent(TRACKING_EVENTS.CREATED_ANY_MANUAL_PREDICTION);
          trackEvent(TRACKING_EVENTS.CREATE_PREDICTION_FREESTYLE_NEW);
          return true;
        }
      }
    }

    return false;
  }, [
    accountIndex,
    freestyleContent,
    handleSaveExisting,
    handleSaveNew,
    isQuickCopy,
    paymentDataSaveParams,
    paymentTreatment,
    predictionName,
    saveAssumptionData,
    storeOnThe,
    urlPredictionId,
    vatPercentage
  ]);

  // ************
  // * Handlers *
  // ************

  const handleFreestyleContentChange = useCallback(
    (content: FreestyleMethodState) => {
      if (!checkIfFreestyleContentIsEmpty(content)) {
        setPredictionContentTouched(true);
      }
      setFreestyleContent(content);
    },
    [setPredictionContentTouched]
  );

  const handleAddFinancialYear = useCallback(() => {
    if (freestyleContent && financialConfig) {
      const lastFinancialYear = Object.keys(freestyleContent)
        .map(k => Number(k))
        .sort((a, b) => b - a)[0];

      const newContent = {
        ...freestyleContent,
        ...generateFinancialYearsData(financialConfig, lastFinancialYear + 1, lastFinancialYear + 1)
      };

      handleFreestyleContentChange(newContent);
    }
  }, [freestyleContent, financialConfig, handleFreestyleContentChange]);

  const handleSave = useCallback(async () => {
    addCreating('freestyle-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      onClose?.();
    }
    removeCreating('freestyle-prediction');
  }, [addCreating, save, removeCreating, reloadForecastData, onClose]);

  const handleSaveAndNew = useCallback(async () => {
    addCreating('freestyle-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      reset();
      resetUrlData();
      restoreInitialContent();
    }
    removeCreating('freestyle-prediction');
  }, [addCreating, reloadForecastData, removeCreating, reset, restoreInitialContent, save, resetUrlData]);

  const handleStoreOnTheChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStoreOnThe(e.target.value);
    setPredictionContentTouched(true);
  };

  // ***********
  // * Effects *
  // ***********

  // Load values (if editing)
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Set initial / restore saved content on first run
  useEffect(() => {
    if (isFirstRun.current) {
      const contentWasRestored =
        (isEditing || isQuickCopy) && isActive ? restoreLoadedContent() : restoreInitialContent();
      if (contentWasRestored) {
        isFirstRun.current = false;
      }
    }
  }, [
    financialConfig,
    forecast,
    freestyleValues,
    isActive,
    isEditing,
    predictionData,
    restoreInitialContent,
    restoreLoadedContent,
    isQuickCopy
  ]);

  // Refresh the accounts impacted info
  useEffect(() => {
    if (isActive) {
      refreshAccountsImpactPreview();
    } else {
      lastImpactPreviewParams.current = undefined;
    }
  }, [isActive, refreshAccountsImpactPreview]);

  // Update isSaveDisabled flag
  useEffect(() => {
    if (isActive) {
      setIsSaveDisabled(false);
    }
  }, [isActive, setIsSaveDisabled]);

  return (
    <PredictionEditor
      onSave={handleSave}
      onSaveAndNew={handleSaveAndNew}
      show={isActive}
      onClose={onClose}
      isPredictionTypeActive={isPredictionTypeActive}
    >
      <FreestyleContent
        currency={currency}
        freestyleContent={freestyleContent}
        handleStoreOnTheChange={handleStoreOnTheChange}
        onAddFinancialYear={handleAddFinancialYear}
        onChange={handleFreestyleContentChange}
        PredictionHeader={PredictionHeader}
        storeOnThe={storeOnThe}
        previewImpacts={previewImpactsResponse}
      />
    </PredictionEditor>
  );
};
